import React, { useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Checkbox, TextField, MenuItem,
  Chip, CircularProgress, Alert, Tooltip, Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptIcon from '@mui/icons-material/Receipt';

const ANIOS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
const MESES = [
  { v: 1, l: 'Enero' }, { v: 2, l: 'Febrero' }, { v: 3, l: 'Marzo' },
  { v: 4, l: 'Abril' }, { v: 5, l: 'Mayo' }, { v: 6, l: 'Junio' },
  { v: 7, l: 'Julio' }, { v: 8, l: 'Agosto' }, { v: 9, l: 'Septiembre' },
  { v: 10, l: 'Octubre' }, { v: 11, l: 'Noviembre' }, { v: 12, l: 'Diciembre' },
];

const TIPOS_CC = [
  { value: '01', label: '01 — Acepta propuesta SUNAT' },
  { value: '02', label: '02 — Reemplaza propuesta' },
  { value: '03', label: '03 — Ajustes posteriores al período' },
  { value: '04', label: '04 — Ajustes períodos anteriores — Formato General' },
  { value: '05', label: '05 — Ajustes períodos anteriores — Formato Simplificado' },
];

const TIPO_MAP = {
  '01': 'Factura', '03': 'Boleta', '07': 'NC', '08': 'ND',
  '09': 'Guía', '02': 'RHE', '04': 'Liq. Compra',
};

function fmtMon(v) {
  return Number(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtFecha(d) {
  if (!d) return '';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
}
function chipEstado(e) {
  if (e === 'ACEPTADO') return <Chip label="ACEPTADO" color="success" size="small" />;
  if (e === 'ANULADO')  return <Chip label="ANULADO"  color="error"   size="small" />;
  if (e === 'ENVIADO')  return <Chip label="ENVIADO"  color="info"    size="small" />;
  return <Chip label={e || '—'} size="small" />;
}

export default function SireVentas() {
  const now = new Date();
  const [anio,      setAnio]      = useState(now.getFullYear());
  const [mes,       setMes]       = useState(now.getMonth() + 1);
  const [tipoCC,    setTipoCC]    = useState('02');
  const [corr,      setCorr]      = useState('01');
  const [rows,      setRows]      = useState([]);
  const [selected,  setSelected]  = useState(new Set());
  const [loading,   setLoading]   = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error,     setError]     = useState('');

  const esAjuste = ['03', '04', '05'].includes(tipoCC);

  const handleCargar = useCallback(async () => {
    setLoading(true);
    setError('');
    setSelected(new Set());
    try {
      const res = await axios.get(`/api/sire/ventas?anio=${anio}&mes=${mes}`);
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar datos');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [anio, mes]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(new Set(rows.map(r => r.id)));
    } else {
      setSelected(new Set());
    }
  };

  const handleSelectRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDescargar = async (formato) => {
    if (selected.size === 0) return;
    setGenerando(true);
    try {
      const idsParam = [...selected].join(',');
      const corrParam = esAjuste ? String(corr).padStart(2, '0') : '00';
      const url = `/api/sire/ventas/descargar?anio=${anio}&mes=${mes}&tipo=${tipoCC}&formato=${formato}&ids=${idsParam}&corr=${corrParam}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const fnMatch = cd.match(/filename="([^"]+)"/);
      const filename = fnMatch ? fnMatch[1] : `SIRE_RVIE_${anio}${String(mes).padStart(2,'0')}.${formato}`;
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setError('Error al generar el archivo');
    } finally {
      setGenerando(false);
    }
  };

  const selRows = rows.filter(r => selected.has(r.id));
  const totBI    = selRows.reduce((s, r) => s + Number(r.total_gravada  || 0), 0);
  const totIGV   = selRows.reduce((s, r) => s + Number(r.total_igv      || 0), 0);
  const totTotal = selRows.reduce((s, r) => s + Number(r.total          || 0), 0);
  const allSelected = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && selected.size < rows.length;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Encabezado */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <ReceiptIcon color="success" />
        <Typography variant="h6" fontWeight="bold">SIRE — Ventas (RVIE)</Typography>
        <Chip label="Registro de Ventas e Ingresos Electrónico" size="small" color="success" variant="outlined" />
      </Box>

      {/* Filtros */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            select label="Año" value={anio} onChange={e => setAnio(Number(e.target.value))}
            size="small" sx={{ minWidth: 100 }}
          >
            {ANIOS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>

          <TextField
            select label="Mes" value={mes} onChange={e => setMes(Number(e.target.value))}
            size="small" sx={{ minWidth: 130 }}
          >
            {MESES.map(m => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
          </TextField>

          <Button
            variant="contained" color="primary" startIcon={<SearchIcon />}
            onClick={handleCargar} disabled={loading}
            sx={{ height: 40 }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : 'Cargar'}
          </Button>

          <Divider orientation="vertical" flexItem />

          <TextField
            select label="Tipo de oportunidad (CC)" value={tipoCC}
            onChange={e => setTipoCC(e.target.value)}
            size="small" sx={{ minWidth: 310 }}
          >
            {TIPOS_CC.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>

          {esAjuste && (
            <TextField
              label="Correlativo ajuste (NN)" value={corr}
              onChange={e => setCorr(e.target.value.replace(/\D/g, '').slice(0, 2))}
              size="small" sx={{ minWidth: 160 }}
              inputProps={{ maxLength: 2 }}
              helperText="2 dígitos (ej: 01)"
            />
          )}

          <Tooltip title={selected.size === 0 ? 'Selecciona al menos 1 comprobante' : ''}>
            <span>
              <Button
                variant="outlined" color="primary" startIcon={<DownloadIcon />}
                onClick={() => handleDescargar('txt')}
                disabled={selected.size === 0 || generando}
                sx={{ height: 40 }}
              >
                {generando ? <CircularProgress size={16} /> : 'TXT'}
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={selected.size === 0 ? 'Selecciona al menos 1 comprobante' : ''}>
            <span>
              <Button
                variant="outlined" color="success" startIcon={<TableChartIcon />}
                onClick={() => handleDescargar('excel')}
                disabled={selected.size === 0 || generando}
                sx={{ height: 40 }}
              >
                Excel
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Resumen selección */}
      {rows.length > 0 && (
        <Box sx={{ display: 'flex', gap: 3, mb: 1, px: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            <b>{rows.length}</b> comprobante(s) en el período
          </Typography>
          <Typography variant="body2" color="primary">
            <b>{selected.size}</b> seleccionado(s)
          </Typography>
          {selected.size > 0 && (
            <>
              <Typography variant="body2">Base Gravada: <b>S/ {fmtMon(totBI)}</b></Typography>
              <Typography variant="body2">IGV: <b>S/ {fmtMon(totIGV)}</b></Typography>
              <Typography variant="body2" color="success.main">Total: <b>S/ {fmtMon(totTotal)}</b></Typography>
            </>
          )}
        </Box>
      )}

      {/* Tabla */}
      {rows.length > 0 && (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                    color="primary"
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Serie</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Número</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Doc. Identidad</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Base Grav.</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>IGV</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => {
                const isSelected = selected.has(r.id);
                const tipoCdp = r.tipo_comprobante;
                const tipoLabel = TIPO_MAP[tipoCdp] || tipoCdp || '—';
                return (
                  <TableRow
                    key={r.id}
                    hover
                    selected={isSelected}
                    onClick={() => handleSelectRow(r.id)}
                    sx={{ cursor: 'pointer', opacity: r.estado_cpe === 'ANULADO' ? 0.6 : 1 }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isSelected} color="primary" onClick={e => e.stopPropagation()} onChange={() => handleSelectRow(r.id)} />
                    </TableCell>
                    <TableCell>
                      <Chip label={tipoLabel} size="small" variant="outlined"
                        color={tipoCdp === '01' ? 'primary' : tipoCdp === '03' ? 'success' : tipoCdp === '07' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.serie}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{String(r.numero || '').padStart(8, '0')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtFecha(r.fecha_emision)}</TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.cliente_nombre || '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.cliente_num_doc || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMon(r.total_gravada)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMon(r.total_igv)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>{fmtMon(r.total)}</TableCell>
                    <TableCell>{chipEstado(r.estado_cpe)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && rows.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Typography color="text.secondary">
            Selecciona año y mes, luego presiona <b>Cargar</b> para ver los comprobantes del período.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
